import React from 'react';
import {
  ModalOverlay,
  ModalCard,
  ModalTitle,
  ModalActions,
  FormGroup,
  Label,
  Input,
  Select,
  Textarea,
  Button,
  SecondaryButton,
  VariableChip
} from './StyledComponents';

// Add Section Modal
export const AddSectionModal = ({ 
  isOpen, 
  onClose, 
  sectionName, 
  setSectionName, 
  sectionType, 
  setSectionType, 
  onAdd, 
  isLoading,
  error 
}) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalCard>
        <ModalTitle>Add New Section</ModalTitle>
        
        {error && <div style={{ color: '#dc2626', marginBottom: '12px' }}>{error}</div>}
        
        <FormGroup>
          <Label>Section Name (Key)</Label>
          <Input
            type="text"
            value={sectionName}
            onChange={(e) => setSectionName(e.target.value)}
            placeholder="Enter a unique name for this section"
          />
        </FormGroup>
        
        <FormGroup>
          <Label>Section Type</Label>
          <Select
            value={sectionType}
            onChange={(e) => setSectionType(e.target.value)}
          >
            <option value="">Select a section type</option>
            <option value="anchor">Anchor (For Each Anchor)</option>
            <option value="lowestScore">Lowest Score (For Each)</option>
            <option value="highestScore">Highest Score (For Each)</option>
            <option value="biggestPositiveChange">Biggest Positive Change (For Each)</option>
            <option value="biggestNegativeChange">Biggest Negative Change (For Each)</option>
            <option value="factorScoreByHighest">Factor Score by Highest (For Each)</option>
            <option value="factorDistributionHighest">Factor Distribution Scores by Highest (For Each)</option>
            <option value="factorDistributionLowest">Factor Distribution Scores by Lowest (For Each)</option>
            <option value="outcomeForEach">Outcome (For Each)</option>
            <option value="outcomeInsight">Outcome Insight (Outcome)</option>
            <option value="overview_LLM">Overview LLM</option>
          </Select>
        </FormGroup>
        
        <ModalActions>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <Button 
            onClick={onAdd}
            disabled={!sectionName || !sectionType || isLoading}
          >
            {isLoading ? 'Adding...' : 'Add Section'}
          </Button>
        </ModalActions>
      </ModalCard>
    </ModalOverlay>
  );
};

// Add Nested Section Modal
export const AddNestedSectionModal = ({ 
  isOpen, 
  onClose, 
  sectionName, 
  setSectionName, 
  sectionType, 
  setSectionType, 
  onAdd, 
  isLoading,
  error 
}) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalCard>
        <ModalTitle>Add Nested Section</ModalTitle>
        
        {error && <div style={{ color: '#dc2626', marginBottom: '12px' }}>{error}</div>}
        
        <FormGroup>
          <Label>Nested Section Name (Key)</Label>
          <Input
            type="text"
            value={sectionName}
            onChange={(e) => setSectionName(e.target.value)}
            placeholder="Enter a unique name for this nested section"
          />
        </FormGroup>
        
        <FormGroup>
          <Label>Nested Section Type</Label>
          <Select
            value={sectionType}
            onChange={(e) => setSectionType(e.target.value)}
          >
            <option value="">Select a section type</option>
            <option value="lowestScore">Lowest Score (For Each)</option>
            <option value="highestScore">Highest Score (For Each)</option>
            <option value="biggestPositiveChange">Biggest Positive Change (For Each)</option>
            <option value="biggestNegativeChange">Biggest Negative Change (For Each)</option>
            <option value="factorScoreByHighest">Factor Score by Highest (For Each)</option>
            <option value="factorDistributionHighest">Factor Distribution Scores by Highest (For Each)</option>
            <option value="factorDistributionLowest">Factor Distribution Scores by Lowest (For Each)</option>
            <option value="outcomeForEach">Outcome (For Each)</option>
            <option value="outcomeInsight">Outcome Insight (Outcome)</option>
          </Select>
        </FormGroup>
        
        <ModalActions>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <Button 
            onClick={onAdd}
            disabled={!sectionName || !sectionType || isLoading}
          >
            {isLoading ? 'Adding...' : 'Add Nested Section'}
          </Button>
        </ModalActions>
      </ModalCard>
    </ModalOverlay>
  );
};

// Add/Edit LLM Action Modal
export const LLMActionModal = ({ 
  isOpen, 
  onClose, 
  isEditing,
  actionName, 
  setActionName, 
  actionPrompt, 
  setActionPrompt, 
  feedbackType, 
  setFeedbackType, 
  enabled, 
  setEnabled, 
  formatAsJson, 
  setFormatAsJson, 
  onSave, 
  isLoading,
  error,
  sectionType
}) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalCard>
        <ModalTitle>{isEditing ? 'Edit LLM Action' : 'Add LLM Action'}</ModalTitle>
        
        {error && <div style={{ color: '#dc2626', marginBottom: '12px' }}>{error}</div>}
        
        <FormGroup>
          <Label>Action Name (Key)</Label>
          <Input
            type="text"
            value={actionName}
            onChange={(e) => setActionName(e.target.value)}
            placeholder="Enter a unique name for this action (used as key)"
          />
        </FormGroup>
        
        <FormGroup>
          <Label>Feedback Type</Label>
          <Select
            value={feedbackType}
            onChange={(e) => setFeedbackType(e.target.value)}
          >
            <option value="current">Current Feedback</option>
            <option value="last">Last Feedback</option>
            <option value="both">Both Current and Last</option>
            {sectionType === 'outcomeForEach' && (
              <option value="highestDriverCorrelation">Highest Category Correlation</option>
            )}
          </Select>
        </FormGroup>
        
        <FormGroup>
          <Label>Prompt Template</Label>
          <div style={{ marginBottom: '8px' }}>
            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
              Available variables to inject:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
              <VariableChip onClick={() => setActionPrompt(prev => prev + '{{feedback}}')}>
                {'{{feedback}}'}
              </VariableChip>
              <VariableChip onClick={() => setActionPrompt(prev => prev + '{{scores}}')}>
                {'{{scores}}'}
              </VariableChip>
              <VariableChip onClick={() => setActionPrompt(prev => prev + '{{factorNames}}')}>
                {'{{factorNames}}'}
              </VariableChip>
              <VariableChip onClick={() => setActionPrompt(prev => prev + '{{name}}')}>
                {'{{name}}'}
              </VariableChip>
              <VariableChip onClick={() => setActionPrompt(prev => prev + '{{change}}')}>
                {'{{change}}'}
              </VariableChip>
              <VariableChip onClick={() => setActionPrompt(prev => prev + '{{benchmark}}')}>
                {'{{benchmark}}'}
              </VariableChip>
              <VariableChip onClick={() => setActionPrompt(prev => prev + '{{outcomeScores}}')}>
                {'{{outcomeScores}}'}
              </VariableChip>
            </div>
          </div>
          <Textarea
            value={actionPrompt}
            onChange={(e) => setActionPrompt(e.target.value)}
            placeholder="Enter your prompt template. Click variables above to inject them, or type {{variableName}} manually."
            style={{ minHeight: '240px' }}
          />
        </FormGroup>
        
        <FormGroup>
          <Label>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            {' '}Enable this action
          </Label>
        </FormGroup>
        
        <FormGroup>
          <Label>
            <input
              type="checkbox"
              checked={formatAsJson}
              onChange={(e) => setFormatAsJson(e.target.checked)}
            />
            {' '}Format output as JSON
          </Label>
        </FormGroup>
        
        <ModalActions>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <Button 
            onClick={onSave}
            disabled={!actionName || !actionPrompt || isLoading}
          >
            {isLoading ? 'Saving...' : 'Save LLM Action'}
          </Button>
        </ModalActions>
      </ModalCard>
    </ModalOverlay>
  );
};

// Edit Section Config Modal
export const EditSectionConfigModal = ({ 
  isOpen, 
  onClose, 
  configField, 
  setConfigField, 
  configValue, 
  setConfigValue, 
  onSave, 
  isLoading 
}) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalCard>
        <ModalTitle>Edit Section Config</ModalTitle>
        
        <FormGroup>
          <Label>Field to Edit</Label>
          <Select
            value={configField}
            onChange={(e) => setConfigField(e.target.value)}
          >
            <option value="">Select a field</option>
            <option value="num">Number of items</option>
            <option value="includeOutcomeScores">Include outcome scores</option>
          </Select>
        </FormGroup>
        
        {configField === 'num' && (
          <FormGroup>
            <Label>New Value (default: 3)</Label>
            <Input
              type="number"
              value={configValue || 3}
              onChange={(e) => setConfigValue(parseInt(e.target.value) || 3)}
              min="1"
              max="10"
            />
          </FormGroup>
        )}
        
        {configField === 'includeOutcomeScores' && (
          <FormGroup>
            <Label>New Value</Label>
            <Select
              value={configValue || false}
              onChange={(e) => setConfigValue(e.target.value === 'true')}
            >
              <option value="true">True</option>
              <option value="false">False</option>
            </Select>
          </FormGroup>
        )}
        
        <ModalActions>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <Button 
            onClick={onSave}
            disabled={!configField || configValue === undefined || isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Config'}
          </Button>
        </ModalActions>
      </ModalCard>
    </ModalOverlay>
  );
};

// Confirmation Modal
export const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  title, 
  message, 
  onConfirm, 
  confirmText = 'Confirm',
  isLoading = false 
}) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalCard>
        <ModalTitle>{title}</ModalTitle>
        <p>{message}</p>
        <ModalActions>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <Button onClick={onConfirm} disabled={isLoading}>
            {isLoading ? 'Processing...' : confirmText}
          </Button>
        </ModalActions>
      </ModalCard>
    </ModalOverlay>
  );
}; 